<template>
    <div class="content">
        <div class="md-layout">
            <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
                <div class="custom-toolbar md-layout">
                    <div class="md-layout-item custom-toolbar-start md-layout md-small-size-80 md-size-90">
                        <team-members-filter :default-value="initialTeamMember" @onSelection="onTeamMemberSelection" />

                        <div class="md-layout-item">
                            <date-range-filter
                                :is-single-date="true"
                                :default-date="initialDate"
                                @onChanged="onDateChanged"
                            />
                        </div>
                    </div>
                    <div class="md-layout-item custom-toolbar-end">
                        <md-button
                            title="Export Location History report"
                            class="md-primary md-just-icon md-round pull-right header-button"
                            @click="onExportToExcel"
                        >
                            <md-icon>get_app</md-icon>
                        </md-button>
                    </div>
                </div>
            </div>
            <div class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-100">
                <md-card>
                    <md-card-header class="md-card-header-icon md-card-header-blue">
                        <div class="card-icon">
                            <md-icon>summarize</md-icon>
                        </div>
                    </md-card-header>

                    <md-card-content>
                        <div v-if="!reports || reports.length == 0">
                            <h3>Location History Report</h3>
                            <p>
                                No results to display.
                            </p>
                        </div>
                        <div v-else>
                            <div class="report-container">
                                <md-table :value="reports" class="table-hover">
                                    <md-table-row
                                        slot="md-table-row"
                                        slot-scope="{ item, index }"
                                        @click="viewAddress(item)"
                                    >
                                        <md-table-cell>
                                            {{ index + 1 }}
                                        </md-table-cell>
                                        <md-table-cell md-label="Suburb/Town" md-sort>
                                            <a class="address" @click.stop="viewAddress(item)">
                                                {{ item.suburb }}
                                            </a>
                                        </md-table-cell>
                                        <md-table-cell md-label="Entered Suburb">
                                            {{ item.timeEntered | timeFormat(DATE_TYPES.standardTime) }}
                                        </md-table-cell>
                                        <md-table-cell md-label="Left Suburb">
                                            {{ item.timeLeft | timeFormat(DATE_TYPES.standardTime) }}
                                        </md-table-cell>
                                    </md-table-row>
                                </md-table>
                            </div>
                        </div>
                    </md-card-content>
                </md-card>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import { mapMutations, mapGetters } from 'vuex';
import { GeneralMixin } from '@/mixins/GeneralMixin';
import { handleRequests, showErrorMessage, downloadFile } from '@/helpers';
import ViewAddressModal from '@/pages/ViewAddressModal';
import { TeamMembersFilter, DateRangeFilter } from '../components';

export default {
    name: 'GpsLocations',
    components: { TeamMembersFilter, DateRangeFilter },
    mixins: [GeneralMixin],
    data() {
        const savedArgs = localStorage.getItem('gps-reports.search-args');
        const parsedArgs = savedArgs ? JSON.parse(savedArgs) : null;

        return {
            localStorageKey: 'gps-reports.search-args',
            searchArgs: {
                eventSourceId: parsedArgs?.eventSourceId || null,
                eventDate: parsedArgs?.eventDate || null,
                name: parsedArgs?.name || '',
                isTracker: parsedArgs?.isTracker || false,
                teamRegionId: parsedArgs?.teamRegionId !== undefined ? parsedArgs.teamRegionId : null
            },
            initialTeamMember: parsedArgs
                ? {
                    eventSourceId: parsedArgs.eventSourceId,
                    isTracker: parsedArgs.isTracker || false,
                    teamRegionId: parsedArgs.teamRegionId !== undefined ? parsedArgs.teamRegionId : null
                }
                : null,
            initialDate: parsedArgs?.eventDate || null,
            reports: []
        };
    },
    methods: {
        ...mapGetters({
            user: 'user/user'
        }),
        ...mapMutations({
            changeDate: 'map/CHANGE_DATE'
        }),

        async onExportToExcel() {
            if (!this.searchArgs.eventSourceId || !this.searchArgs.eventDate) 
                return;

            try {
                const { eventSourceId, eventDate, isTracker } = this.searchArgs;
                const eventDateStr = moment(eventDate).format('YYYY-MM-DD');

                const api = `/api/gps-reports/${eventSourceId}/locations/export-to-excel?eventDate=${eventDateStr}&isTracker=${isTracker}`;
                const payload = {
                    method: 'get',
                    responseType: 'blob'
                };

                this.$_handleLoaderState(true);

                const { data } = await handleRequests(api, payload);
                const reportName = `${this.searchArgs.name}-locations-${eventDateStr}.xlsx`;
                downloadFile(data, reportName);
            } catch (error) {
                console.error('Error exporting to Excel:', error);
                showErrorMessage(this, 'Error exporting to Excel.', error);
            } finally {
                this.$_handleLoaderState(false);
            }
        },
        async getReports() {
            if (!this.searchArgs.eventSourceId || !this.searchArgs.eventDate) 
                return;
            localStorage.setItem(this.localStorageKey, JSON.stringify(this.searchArgs));

            try {
                const { eventSourceId, eventDate, isTracker } = this.searchArgs;
                const eventDateStr = moment(eventDate).format('YYYY-MM-DD');

                const api = `/api/gps-reports/${eventSourceId}/locations?eventDate=${eventDateStr}&isTracker=${isTracker}`;

                this.$_handleLoaderState(true);

                const { data } = await handleRequests(api);
                this.reports = data || [];
            } catch (error) {
                console.error('Error fetching reports:', error);
                showErrorMessage(this, 'Failed to fetch reports.', error);
            } finally {
                this.$_handleLoaderState(false);
            }
        },
        async viewAddress(item) {
            try {
                const query = new URLSearchParams();
                query.append('latitude', item.latEntered);
                query.append('longitude', item.lngEntered);
                query.append('level', 16);

                const api = `/api/location/boundaries?${query.toString()}`;

                this.$_handleLoaderState(true);
                const { data } = await handleRequests(api);

                if (data?.data) {
                    // The above polygon data are returned in the natural JSON format by the Nominatim service (we only return the geojson field).
                    // Calling the following link on the web browser for a coordinate around Sydney(https://nominatim.openstreetmap.org/reverse.php?format=json&lat=-33.879143800971704&lon=151.16402717937993&zoom=14&extratags=1&polygon_geojson=1 )
                    // we get JSON (polygon) data in double nested array (check it out).
                    // It returns a polygon but the polygon is nested inside a double nested array. Each coordinate of that polygon is also in format longitude, latitude & not latitude, longitude.
                    // The problem is that Nominatim might return an area of type Polygon or MultiPolygon, etc. More details on:
                    // https://gis.stackexchange.com/questions/270778/nominatim-geojson
                    // https://gis.stackexchange.com/questions/221448/query-country-border-polygon-from-openstreetmap
                    //
                    // Currently we don't handle MultiPolygons as there are very limited places worldwide (and mostly represent country areas) that are using them.
                    // But if we need to, we can implement handling of them on a later stage (even though they are tricky).
                    // The following link has details about them of how OpenStreetMaps handle them with XML although I am not quite sure of how Nominatim does it (haven't found any details).
                    // https://wiki.openstreetmap.org/wiki/Relation:multipolygon

                    // Format returned is an array of polygons that have arrays of coords. The coords are [lat, long] in array & not object.
                    // eg: [ [[x1,y1],[x2,y2],[x3,y3]], [[x4,y4],[x5,y5],...] ]
                    // We need to convert it to: [ [{lat: y1, lng: x1}, {lat: y2, lng: x2}, {lat: y3, lng: x3}], [{lat: y4, lng: x4}, ...] ]
                    const polygons = JSON.parse(data.data.coordinates).map((d) =>
                        d.map((d2) => ({ lat: d2[1], lng: d2[0] }))
                    );

                    this.$modal.show(ViewAddressModal, {
                        data: {
                            location: {
                                latitude: item.latEntered,
                                longitude: item.lngEntered
                            },
                            address: item.addressEntered,
                            polygons: {
                                data: polygons,
                                options: {
                                    strokeColor: '#FF0000',
                                    strokeOpacity: 0.8,
                                    strokeWeight: 2,
                                    fillColor: '#FF0000',
                                    fillOpacity: 0.15
                                }
                            }
                        }
                    });
                } else {
                    this.$modal.show(ViewAddressModal, {
                        data: {
                            location: {
                                latitude: item.latEntered,
                                longitude: item.lngEntered
                            },
                            address: item.addressEntered
                        }
                    });
                }

                this.$_handleLoaderState(true);
            } catch (error) {
                console.error('Error fetching address details:', error);
                showErrorMessage(this, 'Failed to fetch address details.', error);
            } finally {
                this.$_handleLoaderState(false);
            }
        },
        async onDateChanged(val) {
            if (!val) {
                this.searchArgs.eventDate = null;
                return;
            }

            this.searchArgs.eventDate = val;
            this.getReports();
        },

        async onTeamMemberSelection(val) {
            // Always save teamRegionId when it's provided
            if (val?.teamRegionId !== undefined) {
                this.searchArgs.teamRegionId = val.teamRegionId;
            }

            if (!val?.userId) {
                this.searchArgs.eventSourceId = null;
                this.searchArgs.name = '';
                return;
            }

            this.searchArgs.eventSourceId = val.userId; // userId is either publicUserId or trackerReference
            this.searchArgs.name = val.name || '';
            this.searchArgs.isTracker = val.isTracker || false;
            this.getReports();
        }
    }
};
</script>

<style lang="scss" scoped>
::v-deep .md-table-head-label {
    color: #4caf50 !important;
}

::v-deep .md-table-head {
    cursor: default;
}

.table-hover {
    cursor: pointer;
}
</style>
